import React, { useMemo } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { RAF_FIELD_SECTIONS, computeSectionCompletion } from '../../views/matters/raf-field-sections';

interface Props {
  extracted: Record<string, unknown> | undefined;
  naSections: string[] | undefined;
}

const SectionCompletionPanel: React.FC<Props> = ({ extracted, naSections }) => {
  const rows = useMemo(() => {
    const na = new Set(naSections ?? []);
    return RAF_FIELD_SECTIONS.map((section) => {
      const isNa = na.has(section.id);
      const { filled, total, pct } = computeSectionCompletion(section, extracted, isNa);
      return { id: section.id, label: section.label, total, filled, pct, isNa };
    });
  }, [extracted, naSections]);

  return (
    <Box
      sx={{
        bgcolor: 'var(--bg)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1.25,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--t1)', letterSpacing: '0.005em' }}>
          Section Completion
        </Typography>
      </Box>
      <Box sx={{ px: 1.75, py: 1.25, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {rows.map((r) => (
          <Box key={r.id}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.375 }}>
              <Typography
                sx={{
                  fontSize: '0.74rem',
                  color: r.isNa ? 'var(--t4)' : 'var(--t2)',
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  pr: 1,
                }}
              >
                {r.label}
                {r.isNa ? ' · N/A' : ''}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.66rem',
                  color: 'var(--t3)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  flexShrink: 0,
                }}
              >
                {r.isNa ? '—' : `${r.filled}/${r.total}`}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={r.isNa ? 0 : r.pct}
              sx={{
                height: 3,
                borderRadius: 2,
                bgcolor: 'var(--s2)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: r.isNa
                    ? 'var(--t4)'
                    : r.pct >= 80
                      ? 'var(--ok)'
                      : r.pct >= 40
                        ? 'var(--teal)'
                        : 'var(--warn)',
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SectionCompletionPanel;
